import { CoreState } from '@app/core/store/core.slice';
import { createLoadActions } from '@app/core/store/_prototypes/action-reducers/actions/load.actions';
import { createLoadReducer } from '@app/core/store/_prototypes/action-reducers/reducers/load.reducers';
import { loadSelector } from '@app/core/store/_prototypes/action-reducers/selectors/load.selectors';
import {
  initializeLoadStateWithoutData,
  LoadState,
} from '@app/core/store/_prototypes/action-reducers/state/load.state';
import {
  Company,
  EmergencyContact,
  FinancialContact,
  Personal,
  ProfilePictureData,
  BirthPlace,
  Address,
  AddressUpsert,
} from '@app/core/types/profile/profile.types';
import { combineReducers, createSelector, on, props } from '@ngrx/store';
import { User } from '@app/core/types/membership/membership.types';
import { VerificationChannel } from '@app/core/types/membership/membership.types';

const PREFIX = '[PROFILE]';
export const stateKey = 'profile';

export interface ProfileState {
  rNumber: LoadState<Record<never, never>, string>;
  picture: LoadState<Record<never, never>, ProfilePictureData>;
  company: LoadState<Record<never, never>, Company>;
  user: LoadState<Record<never, never>, User>;
  personal: LoadState<Record<never, never>, Personal>;
  birthPlace: LoadState<Record<never, never>, BirthPlace>;
  emergencyContact: LoadState<Record<never, never>, EmergencyContact>;
  financialContact: LoadState<Record<never, never>, FinancialContact>;
  address: LoadState<Record<never, never>, Address>;
  deleteAccount: LoadState<{ data: string }, void>;
  update: {
    picture: LoadState<{ data: ProfilePictureData }, string>;
    company: LoadState<{ data: Company }, Company>;
    personal: LoadState<{ data: Personal }, Personal>;
    birthPlace: LoadState<{ data: BirthPlace }, BirthPlace>;
    address: LoadState<{ data: AddressUpsert }, Address>;
    emergencyContact: LoadState<{ data: EmergencyContact }, EmergencyContact>;
    financialInformation: {
      contact: LoadState<{ contact: FinancialContact }, FinancialContact>;
    };
    email: {
      start: LoadState<{ email: string }, void>;
      verify: LoadState<{ email: string; verificationCode: string }, boolean>;
      change: LoadState<{ email: string; verificationCode: string; eTag: string }, User>;
    };
    phone: {
      start: LoadState<{ phoneNumber: string; channel: VerificationChannel }, void>;
      verify: LoadState<{ phoneNumber: string; verificationCode: string }, boolean>;
      change: LoadState<{ phoneNumber: string; verificationCode: string; eTag: string }, User>;
    };
  };
}

export const getRNumber = createLoadActions<Record<string, never>, string>(
  `${PREFIX} getRNumber`,
  props<Record<string, never>>()
);
export const getPicture = createLoadActions<Record<string, never>, ProfilePictureData>(
  `${PREFIX} getPicture`,
  props<Record<string, never>>()
);
export const updatePicture = createLoadActions<{ data: ProfilePictureData }, string>(
  `${PREFIX} updatePicture`,
  props<{ data: ProfilePictureData }>()
);
export const updatePersonal = createLoadActions<{ data: Personal }, Personal>(
  `${PREFIX} updatePersonal`,
  props<{ data: Personal }>()
);
export const getPersonal = createLoadActions<Record<string, never>, Personal>(
  `${PREFIX} getPersonal`,
  props<Record<string, never>>()
);
export const getBirthPlace = createLoadActions<Record<string, never>, BirthPlace>(
  `${PREFIX} getBirthPlace`,
  props<Record<string, never>>()
);
export const updateBirthPlace = createLoadActions<{ data: BirthPlace }, BirthPlace>(
  `${PREFIX} updateBirthPlace`,
  props<{ data: BirthPlace }>()
);
export const getEmergencyContact = createLoadActions<Record<string, never>, EmergencyContact>(
  `${PREFIX} getEmergencyContact`,
  props<Record<string, never>>()
);
export const getFinancialContact = createLoadActions<Record<string, never>, FinancialContact>(
  `${PREFIX} getFinancialContact`,
  props<Record<string, never>>()
);
export const getCompany = createLoadActions<Record<string, never>, Company>(
  `${PREFIX} getCompany`,
  props<Record<string, never>>()
);
export const updateCompany = createLoadActions<{ data: Company }, Company>(
  `${PREFIX} updateCompany`,
  props<{ data: Company }>()
);
export const getUser = createLoadActions<Record<string, never>, User>(
  `${PREFIX} getUser`,
  props<Record<string, never>>()
);
export const getAddress = createLoadActions<Record<string, never>, Address>(
  `${PREFIX} getAddress`,
  props<Record<string, never>>()
);
export const updateAddress = createLoadActions<{ data: AddressUpsert }, Address>(
  `${PREFIX} updateAddress`,
  props<{ data: AddressUpsert }>()
);
export const updateEmergencyContact = createLoadActions<{ data: EmergencyContact }, EmergencyContact>(
  `${PREFIX} updateEmergencyContact`,
  props<{ data: EmergencyContact }>()
);
export const financialInformationContact = createLoadActions<{ contact: FinancialContact }, FinancialContact>(
  `${PREFIX} financialInformationContact`,
  props<{ contact: FinancialContact }>()
);
export const verifyEmail = createLoadActions<{ email: string; verificationCode: string }, boolean>(
  `${PREFIX} verifyEmail`,
  props<{ email: string; verificationCode: string }>()
);
export const startEmailVerification = createLoadActions<{ email: string }, void>(
  `${PREFIX} startEmailVerification`,
  props<{ email: string }>()
);
export const updateEmail = createLoadActions<{ email: string; verificationCode: string; eTag: string }, User>(
  `${PREFIX} updateEmail`,
  props<{ email: string; verificationCode: string; eTag: string }>()
);
export const verifyPhone = createLoadActions<{ phoneNumber: string; verificationCode: string }, boolean>(
  `${PREFIX} verifyPhone`,
  props<{ phoneNumber: string; verificationCode: string }>()
);
export const startPhoneVerification = createLoadActions<{ phoneNumber: string; channel: VerificationChannel }, void>(
  `${PREFIX} startPhoneVerification`,
  props<{ phoneNumber: string; channel: VerificationChannel }>()
);
export const updatePhone = createLoadActions<{ phoneNumber: string; verificationCode: string; eTag: string }, User>(
  `${PREFIX} updatePhone`,
  props<{ phoneNumber: string; verificationCode: string; eTag: string }>()
);
export const deleteAccount = createLoadActions<{ data: string }, void>(
  `${PREFIX} deleteAccount`,
  props<{ data: string }>()
);

export const profileReducers = combineReducers<ProfileState>({
  rNumber: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, string>(), getRNumber),
  picture: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, ProfilePictureData>(), getPicture, [
    on(updatePicture.load.success, (state, action) => ({
      ...state,
      data: { picture: action.loaded.data.picture, eTag: action.data },
    })),
  ]),
  personal: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, Personal>(), getPersonal, [
    on(updatePersonal.load.success, (state, action) => createStateObjectFromResponse(state, action)),
  ]),
  company: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, Company>(), getCompany, [
    on(updateCompany.load.success, (state, action) => createStateObjectFromResponse(state, action)),
  ]),
  birthPlace: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, BirthPlace>(), getBirthPlace, [
    on(updateBirthPlace.load.success, (state, action) => createStateObjectFromResponse(state, action)),
  ]),
  emergencyContact: createLoadReducer(
    initializeLoadStateWithoutData<Record<string, never>, EmergencyContact>(),
    getEmergencyContact,
    [on(updateEmergencyContact.load.success, (state, action) => createStateObjectFromResponse(state, action))]
  ),
  financialContact: createLoadReducer(
    initializeLoadStateWithoutData<Record<string, never>, FinancialContact>(),
    getFinancialContact,
    [on(financialInformationContact.load.success, (state, action) => createStateObjectFromResponse(state, action))]
  ),
  user: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, User>(), getUser, [
    on(updateEmail.load.success, (state, action) => createStateObjectFromResponse(state, action)),
    on(updatePhone.load.success, (state, action) => createStateObjectFromResponse(state, action)),
  ]),
  address: createLoadReducer(initializeLoadStateWithoutData<Record<string, never>, Address>(), getAddress, [
    on(updateAddress.load.success, (state, action) => createStateObjectFromResponse(state, action)),
  ]),
  deleteAccount: createLoadReducer(initializeLoadStateWithoutData<{ data: string }, void>(), deleteAccount),
  update: combineReducers({
    picture: createLoadReducer(initializeLoadStateWithoutData<{ data: ProfilePictureData }, string>(), updatePicture),
    company: createLoadReducer(initializeLoadStateWithoutData<{ data: Company }, Company>(), updateCompany),
    personal: createLoadReducer(initializeLoadStateWithoutData<{ data: Personal }, Personal>(), updatePersonal),
    birthPlace: createLoadReducer(initializeLoadStateWithoutData<{ data: BirthPlace }, BirthPlace>(), updateBirthPlace),
    address: createLoadReducer(initializeLoadStateWithoutData<{ data: AddressUpsert }, Address>(), updateAddress),
    emergencyContact: createLoadReducer(
      initializeLoadStateWithoutData<{ data: EmergencyContact }, EmergencyContact>(),
      updateEmergencyContact
    ),
    financialInformation: combineReducers({
      contact: createLoadReducer(
        initializeLoadStateWithoutData<{ contact: FinancialContact }, FinancialContact>(),
        financialInformationContact
      )
    }),
    email: combineReducers({
      start: createLoadReducer(initializeLoadStateWithoutData<{ email: string }, void>(), startEmailVerification),
      verify: createLoadReducer(
        initializeLoadStateWithoutData<{ email: string; verificationCode: string }, boolean>(),
        verifyEmail
      ),
      change: createLoadReducer(
        initializeLoadStateWithoutData<{ email: string; verificationCode: string; eTag: string }, User>(),
        updateEmail
      ),
    }),
    phone: combineReducers({
      start: createLoadReducer(initializeLoadStateWithoutData<{ phoneNumber: string; channel: VerificationChannel }, void>(), startPhoneVerification),
      verify: createLoadReducer(
        initializeLoadStateWithoutData<{ phoneNumber: string; verificationCode: string }, boolean>(),
        verifyPhone
      ),
      change: createLoadReducer(
        initializeLoadStateWithoutData<{ phoneNumber: string; verificationCode: string; eTag: string }, User>(),
        updatePhone
      ),
    }),
  }),
});

const createStateObjectFromResponse = (state, action) => {
  return {
    ...state,
    data: action.data,
  };
};

const stateSelector = createSelector(
  (state: CoreState) => state,
  (state: CoreState) => state[stateKey] as ProfileState
);

export const rNumberStateSelector = createSelector(stateSelector, (state) => state.rNumber);
export const rNumberState = loadSelector(rNumberStateSelector);

export const pictureSelector = createSelector(stateSelector, (state) => state.picture);
export const pictureState = loadSelector(pictureSelector);

export const personalSelector = createSelector(stateSelector, (state) => state.personal);
export const personalState = loadSelector(personalSelector);

export const birthPlaceSelector = createSelector(stateSelector, (state) => state.birthPlace);
export const birthPlaceState = loadSelector(birthPlaceSelector);

export const emergencyContactSelector = createSelector(stateSelector, (state) => state.emergencyContact);
export const emergencyContactState = loadSelector(emergencyContactSelector);

export const financialContactSelector = createSelector(stateSelector, (state) => state.financialContact);
export const financialContactState = loadSelector(financialContactSelector);

export const companySelector = createSelector(stateSelector, (state) => state.company);
export const companyState = loadSelector(companySelector);

export const userStateSelector = createSelector(stateSelector, (state) => state.user);
export const userState = loadSelector(userStateSelector);
export const userFirstNameSelector = createSelector(
  (state: CoreState) => state[stateKey] as ProfileState | undefined,
  (profile) => profile?.user?.data?.firstName ?? null,
);
export const userPictureSelector = createSelector(
  (state: CoreState) => state[stateKey] as ProfileState | undefined,
  (profile) => profile?.picture?.data?.picture ?? null,
);

export const addressStateSelector = createSelector(stateSelector, (state) => state.address);
export const addressState = loadSelector(addressStateSelector);

export const updateStateSelector = createSelector(stateSelector, (state) => state.update);

export const updatePictureSelector = createSelector(updateStateSelector, (state) => state.picture);
export const updatePictureState = loadSelector(updatePictureSelector);
export const updateCompanySelector = createSelector(updateStateSelector, (state) => state.company);
export const updateCompanyState = loadSelector(updateCompanySelector);
export const updateAddressSelector = createSelector(updateStateSelector, (state) => state.address);
export const updateAddressState = loadSelector(updateAddressSelector);
export const updateEmergencyContactSelector = createSelector(updateStateSelector, (state) => state.emergencyContact);
export const updateEmergencyContactState = loadSelector(updateEmergencyContactSelector);

export const financialInformationStateSelector = createSelector(
  updateStateSelector,
  (state) => state.financialInformation
);
export const financialInformationContactStateSelector = createSelector(
  financialInformationStateSelector,
  (state) => state.contact
);
export const financialInformationContactState = loadSelector(financialInformationContactStateSelector);

export const updateEmailSelector = createSelector(updateStateSelector, (state) => state.email);
export const verifyEmailSelector = createSelector(updateEmailSelector, (state) => state.verify);
export const verifyEmailState = loadSelector(verifyEmailSelector);
export const changeEmailSelector = createSelector(updateEmailSelector, (state) => state.change);
export const changeEmailState = loadSelector(changeEmailSelector);

export const updatePhoneSelector = createSelector(updateStateSelector, (state) => state.phone);
export const verifyPhoneSelector = createSelector(updatePhoneSelector, (state) => state.verify);
export const verifyPhoneState = loadSelector(verifyPhoneSelector);
export const changePhoneSelector = createSelector(updatePhoneSelector, (state) => state.change);
export const changePhoneState = loadSelector(changePhoneSelector);

export const deleteAccountSelector = createSelector(stateSelector, (state) => state.deleteAccount);
export const deleteAccountState = loadSelector(deleteAccountSelector);
